import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="smart-form"
export default class extends Controller {
  static targets = ["input", "submit", "error", "field"]
  static values = {
    autoSave: { type: Boolean, default: false },
    autoSaveDelay: { type: Number, default: 2000 },
    validateOnInput: { type: Boolean, default: true },
    showCharCount: { type: Boolean, default: false }
  }

  connect() {
    this.autoSaveTimer = null
    this.originalValues = new Map()
    this.setupFormEnhancements()
    this.storeOriginalValues()
  }

  disconnect() {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer)
    }
  }

  setupFormEnhancements() {
    // Add enhanced input handling
    this.inputTargets.forEach(input => {
      this.setupInput(input)
    })
    
    // Add form-level enhancements
    this.element.addEventListener('submit', this.handleSubmit.bind(this))
    this.element.addEventListener('keydown', this.handleKeydown.bind(this))
    
    // Setup auto-save if enabled
    if (this.autoSaveValue) {
      this.element.addEventListener('input', this.handleAutoSave.bind(this))
    }
  }

  setupInput(input) {
    // Add real-time validation
    if (this.validateOnInputValue) {
      input.addEventListener('input', () => this.validateField(input))
      input.addEventListener('blur', () => this.validateField(input))
    }
    
    // Add character count for text inputs
    if (this.showCharCountValue && (input.type === 'text' || input.tagName === 'TEXTAREA')) {
      this.addCharacterCount(input)
    }
    
    // Add smart auto-complete for common fields
    this.addSmartAutoComplete(input)
    
    // Add copy/paste enhancements
    input.addEventListener('paste', this.handlePaste.bind(this))
  }

  storeOriginalValues() {
    this.inputTargets.forEach(input => {
      this.originalValues.set(input.name, input.value)
    })
  }

  handleSubmit(event) {
    // Validate all fields before submission
    let isValid = true
    this.inputTargets.forEach(input => {
      if (!this.validateField(input)) {
        isValid = false
      }
    })
    
    if (!isValid) {
      event.preventDefault()
      this.focusFirstError()
      
      if (window.Toast) {
        window.Toast.error('Please fix the errors below before submitting.')
      }
      return false
    }
    
    // Add loading state to submit button
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.innerHTML = `
        <svg class="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Submitting...
      `
    }
  }

  handleKeydown(event) {
    // Cmd/Ctrl + Enter to submit
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault()
      this.element.requestSubmit()
    }
    
    // Escape to reset form
    if (event.key === 'Escape') {
      this.resetForm()
    }
  }

  handleAutoSave(event) {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer)
    }
    
    this.autoSaveTimer = setTimeout(() => {
      this.performAutoSave()
    }, this.autoSaveDelayValue)
  }

  validateField(input) {
    const value = input.value.trim()
    const isRequired = input.hasAttribute('required')
    const fieldContainer = input.closest('.field') || input.parentElement
    
    // Clear previous errors
    this.clearFieldError(fieldContainer)
    
    // Required field validation
    if (isRequired && !value) {
      this.showFieldError(fieldContainer, 'This field is required')
      return false
    }
    
    // Email validation
    if (input.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        this.showFieldError(fieldContainer, 'Please enter a valid email address')
        return false
      }
    }
    
    // URL validation
    if (input.type === 'url' && value) {
      try {
        new URL(value)
      } catch {
        this.showFieldError(fieldContainer, 'Please enter a valid URL')
        return false
      }
    }
    
    // Custom pattern validation
    const pattern = input.getAttribute('pattern')
    if (pattern && value) {
      const regex = new RegExp(pattern)
      if (!regex.test(value)) {
        const title = input.getAttribute('title') || 'Invalid format'
        this.showFieldError(fieldContainer, title)
        return false
      }
    }
    
    // Min/max length validation
    const minLength = input.getAttribute('minlength')
    const maxLength = input.getAttribute('maxlength')
    
    if (minLength && value.length < parseInt(minLength)) {
      this.showFieldError(fieldContainer, `Minimum ${minLength} characters required`)
      return false
    }
    
    if (maxLength && value.length > parseInt(maxLength)) {
      this.showFieldError(fieldContainer, `Maximum ${maxLength} characters allowed`)
      return false
    }
    
    // Show success state
    this.showFieldSuccess(fieldContainer)
    return true
  }

  showFieldError(fieldContainer, message) {
    fieldContainer.classList.add('field-error')
    fieldContainer.classList.remove('field-success')
    
    let errorElement = fieldContainer.querySelector('.field-error-message')
    if (!errorElement) {
      errorElement = document.createElement('div')
      errorElement.className = 'field-error-message text-red-600 text-sm mt-1'
      fieldContainer.appendChild(errorElement)
    }
    
    errorElement.textContent = message
    
    // Add error styling to input
    const input = fieldContainer.querySelector('input, textarea, select')
    if (input) {
      input.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      input.classList.remove('border-green-500', 'focus:border-green-500', 'focus:ring-green-500')
    }
  }

  showFieldSuccess(fieldContainer) {
    fieldContainer.classList.add('field-success')
    fieldContainer.classList.remove('field-error')
    
    // Add success styling to input
    const input = fieldContainer.querySelector('input, textarea, select')
    if (input) {
      input.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      input.classList.add('border-green-500', 'focus:border-green-500', 'focus:ring-green-500')
    }
  }

  clearFieldError(fieldContainer) {
    fieldContainer.classList.remove('field-error', 'field-success')
    
    const errorElement = fieldContainer.querySelector('.field-error-message')
    if (errorElement) {
      errorElement.remove()
    }
    
    // Reset input styling
    const input = fieldContainer.querySelector('input, textarea, select')
    if (input) {
      input.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      input.classList.remove('border-green-500', 'focus:border-green-500', 'focus:ring-green-500')
    }
  }

  addCharacterCount(input) {
    const maxLength = input.getAttribute('maxlength')
    if (!maxLength) return
    
    const countElement = document.createElement('div')
    countElement.className = 'character-count text-xs text-gray-500 mt-1 text-right'
    input.parentElement.appendChild(countElement)
    
    const updateCount = () => {
      const remaining = maxLength - input.value.length
      countElement.textContent = `${input.value.length}/${maxLength}`
      countElement.classList.toggle('text-red-500', remaining < 10)
    }
    
    input.addEventListener('input', updateCount)
    updateCount()
  }

  addSmartAutoComplete(input) {
    const name = input.name || input.id
    
    // Common autocomplete attributes
    const autoCompleteMap = {
      'email': 'email',
      'username': 'username',
      'first_name': 'given-name',
      'last_name': 'family-name',
      'full_name': 'name',
      'phone': 'tel',
      'address': 'street-address',
      'city': 'address-level2',
      'state': 'address-level1',
      'zip': 'postal-code',
      'country': 'country'
    }
    
    if (autoCompleteMap[name] && !input.getAttribute('autocomplete')) {
      input.setAttribute('autocomplete', autoCompleteMap[name])
    }
  }

  handlePaste(event) {
    const input = event.target
    
    // Handle URL pasting for URL inputs
    if (input.type === 'url') {
      setTimeout(() => {
        let url = input.value.trim()
        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
          input.value = 'https://' + url
        }
      }, 0)
    }
  }

  focusFirstError() {
    const firstError = this.element.querySelector('.field-error input, .field-error textarea')
    if (firstError) {
      firstError.focus()
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  performAutoSave() {
    // Check if form has changed
    let hasChanges = false
    this.inputTargets.forEach(input => {
      if (this.originalValues.get(input.name) !== input.value) {
        hasChanges = true
      }
    })
    
    if (!hasChanges) return
    
    // Create auto-save data
    const formData = new FormData(this.element)
    formData.append('auto_save', 'true')
    
    // Dispatch auto-save event
    this.dispatch('autoSave', { detail: { formData } })
    
    // Show auto-save indicator
    this.showAutoSaveIndicator()
  }

  showAutoSaveIndicator() {
    const indicator = document.createElement('div')
    indicator.className = 'auto-save-indicator fixed bottom-4 left-4 bg-green-500 text-white px-3 py-2 rounded-lg text-sm'
    indicator.innerHTML = '<i class="fas fa-check mr-2"></i>Auto-saved'
    
    document.body.appendChild(indicator)
    
    setTimeout(() => {
      indicator.remove()
    }, 2000)
  }

  resetForm() {
    this.inputTargets.forEach(input => {
      const originalValue = this.originalValues.get(input.name)
      if (originalValue !== undefined) {
        input.value = originalValue
      }
      
      // Clear validation states
      const fieldContainer = input.closest('.field') || input.parentElement
      this.clearFieldError(fieldContainer)
    })
  }

  // Public methods
  validate() {
    let isValid = true
    this.inputTargets.forEach(input => {
      if (!this.validateField(input)) {
        isValid = false
      }
    })
    return isValid
  }

  getChangedFields() {
    const changes = {}
    this.inputTargets.forEach(input => {
      const originalValue = this.originalValues.get(input.name)
      if (originalValue !== input.value) {
        changes[input.name] = {
          from: originalValue,
          to: input.value
        }
      }
    })
    return changes
  }
}